import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Moment } from 'moment';
import { forkJoin, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  BulkEodsToSave,
  GetConstructionProjectsQueryParams,
  WbsConfigEdit,
  WbsConfigurationScope,
} from '../construction.types';
import {
  ConstructionProjectFeaturesType,
  ConstructionProjectProductionDays,
  ConstructionProjectSaveModel,
  NonWorkingDays,
  ProjectWorkhours,
} from '../models/construction-project.model';
import {
  constructionProjectFeaturesOrder,
  ConstructionProjectFeatureKey,
  MapLayerEnum,
} from '@construction/construction.enums';
import { ConstructionGeoApiService } from '../../@core/services/api/construction-geo-api.service';
import {
  RoverForm,
  Rover,
} from '@construction/modules/project-edit/modules/construction-devices/components/rovers-list/rovers-list.component';
import { SupplyPlanResponseData } from '@construction/modules/project-edit/modules/logistics/views/supply-plan/supply-plan.type';
import { ReferenceLayer, EquipmentSpecs } from '@construction/modules/project-edit/projects.types';
import { EmptyPackReturnsResponseData } from '@construction/modules/project-edit/modules/logistics/views/empty-pack-returns/empty-pack-returns.type';
import { WeekNumberEquivalentProjectWeekType } from '@construction/modules/project-edit/modules/construction-schedule/schedule.types';
import { ConstructionProjectSignalStore } from '@construction/stores/construction-project/construction-project-signal-store.service';
import { ProjectApiService } from '@core/services/api/project-api.service';
import { SortModelItem } from 'ag-grid-community';
import { ServerSideTableService } from '@construction/shared/rich-table/rich-table/services/server-side-table.service';
import { ConstructionProjectApiService } from '../../@core/services/api/construction-project-api.service';
import { ReferenceLayersApiService } from '@core/services/api/reference-layers-api.service';

export type OrthoParams = {
  page_size: number;
  page: number;
  flight_start_from?: string;
  flight_start_to?: string;
  drone_orthos_id?: string;
  status?: string;
};

export type PanoParams = {
  page_size: number;
  page: number;
  date_from?: string;
  date_to?: string;
  status?: string;
};

export const advancedDetectionFeature = {
  name: ConstructionProjectFeatureKey.ADVANCED_DETECTION,
  owner_enabled: true,
  epc_enabled: true,
  supplier_enabled: true,
  id: 99, // need some ID for sorting in table
  aerial_data: null,
  ground_data: null,
};

export const listOfFeatures: ConstructionProjectFeaturesType[] = [
  {
    name: ConstructionProjectFeatureKey.DRONE_IMAGERY,
    owner_enabled: true,
    epc_enabled: true,
    supplier_enabled: false,
    id: 0,
    aerial_data: null,
    ground_data: null,
  },
  {
    name: ConstructionProjectFeatureKey.LOGISTICS_INVENTORY,
    owner_enabled: true,
    epc_enabled: false,
    supplier_enabled: true,
    id: 1,
    aerial_data: null,
    ground_data: null,
  },
  {
    name: ConstructionProjectFeatureKey.QC_ISSUES,
    owner_enabled: true,
    epc_enabled: true,
    supplier_enabled: false,
    id: 2,
    aerial_data: null,
    ground_data: null,
  },
  {
    name: ConstructionProjectFeatureKey.DIGITAL_TWIN,
    owner_enabled: true,
    epc_enabled: true,
    supplier_enabled: true,
    id: 3,
    aerial_data: {
      epc_enabled: false,
      name: 'aerial_data',
      owner_enabled: true,
      supplier_enabled: false,
    },
    ground_data: { epc_enabled: true, name: 'ground_data', owner_enabled: false, supplier_enabled: false },
  },
  {
    name: ConstructionProjectFeatureKey.PRODUCTION_TRACKING,
    owner_enabled: false,
    epc_enabled: false,
    supplier_enabled: false,
    id: 4,
    aerial_data: null,
    ground_data: null,
  },
  {
    name: ConstructionProjectFeatureKey.EQUIPMENT_TRACKING,
    owner_enabled: false,
    epc_enabled: false,
    supplier_enabled: false,
    id: 5,
    aerial_data: null,
    ground_data: null,
  },
  {
    name: ConstructionProjectFeatureKey.APPROVALS,
    owner_enabled: true,
    epc_enabled: false,
    supplier_enabled: false,
    id: 6,
    aerial_data: null,
    ground_data: null,
  },
  {
    name: ConstructionProjectFeatureKey.OMEGA,
    owner_enabled: true,
    epc_enabled: false,
    supplier_enabled: false,
    id: 7,
    aerial_data: null,
    ground_data: null,
  },
  {
    name: ConstructionProjectFeatureKey.MV_CIRCUIT,
    owner_enabled: true,
    epc_enabled: true,
    supplier_enabled: false,
    id: 8,
    aerial_data: null,
    ground_data: null,
  },
  {
    name: ConstructionProjectFeatureKey.ADVANCED_DETECTION,
    owner_enabled: true,
    epc_enabled: true,
    supplier_enabled: false,
    id: 9,
    aerial_data: null,
    ground_data: null,
  },
  {
    name: ConstructionProjectFeatureKey.TERAFAB,
    owner_enabled: false,
    epc_enabled: true,
    supplier_enabled: false,
    id: 10,
    aerial_data: null,
    ground_data: null,
  },
];

@Injectable({ providedIn: 'root' })
export class ConstructionProjectsService {
  private constructionProjectSignalStore = inject(ConstructionProjectSignalStore);
  private projectApiService = inject(ProjectApiService);
  private geoProjectService = inject(ConstructionGeoApiService);
  private serverSideTableService = inject(ServerSideTableService);
  private constructionProjectApiService = inject(ConstructionProjectApiService);
  private referenceLayersApiService = inject(ReferenceLayersApiService);

  public groupWbsLayers = [
    MapLayerEnum.AREA,
    MapLayerEnum.FEEDERS,
    MapLayerEnum.PCS,
    MapLayerEnum.COMBINER_BOXES,
    MapLayerEnum.SITE,
  ];

  public getConstructionProjects(queryParams: GetConstructionProjectsQueryParams) {
    return this.constructionProjectApiService.getConstructionProjects(queryParams);
  }

  public getConstructionProjectAndUpdateStore(constructionProjectId: string) {
    return this.constructionProjectApiService.getConstructionProject(constructionProjectId).pipe(
      switchMap((constructionProject) => {
        const project$ = this.projectApiService.getSingleProject(constructionProject.project._id);
        const geoProject$ = constructionProject.geo_project_uuid
          ? this.geoProjectService.getProjectData(constructionProject.geo_project_uuid)
          : of(undefined);
        const presetDates$ = this.constructionProjectApiService.getPresetDates(constructionProject._id);

        return forkJoin([project$, geoProject$, presetDates$]).pipe(
          map(([projectRes, geoProject, presetDates]) => {
            constructionProject.project = projectRes.data;
            constructionProject.geoProject = geoProject;
            constructionProject.presetData = presetDates;

            return constructionProject;
          }),
        );
      }),
      tap((response) => {
        this.constructionProjectSignalStore.updateConstructionProjectStore(response);
      }),
    );
  }

  public updateConstructionProject(projectId: string, data: ConstructionProjectSaveModel | undefined) {
    return this.constructionProjectApiService
      .updateConstructionProject(projectId, data)
      .pipe(switchMap(() => this.getConstructionProjectAndUpdateStore(projectId)));
  }

  public updateConstructionProjectToArchived(projectId: string, toArchive: boolean) {
    return this.constructionProjectApiService
      .updateConstructionProjectToArchived(projectId, toArchive)
      .pipe(switchMap(() => this.getConstructionProjectAndUpdateStore(projectId)));
  }

  public updateNonWorkingDays(
    projectId: string,
    nonWorkingDays?: NonWorkingDays,
    projectWorkHours?: ProjectWorkhours,
    productionDates?: ConstructionProjectProductionDays,
  ) {
    return this.constructionProjectApiService
      .updateNonWorkingDays(projectId, nonWorkingDays, projectWorkHours, productionDates)
      .pipe(switchMap(() => this.getConstructionProjectAndUpdateStore(projectId)));
  }

  public submitConstructionProject(projectId: string) {
    return this.constructionProjectApiService
      .submitConstructionProject(projectId)
      .pipe(switchMap(() => this.getConstructionProjectAndUpdateStore(projectId)));
  }

  public verifyConstructionProject(projectId: string, geoProjectUuid: string) {
    return this.constructionProjectApiService
      .verifyConstructionProject(projectId, geoProjectUuid)
      .pipe(switchMap(() => this.getConstructionProjectAndUpdateStore(projectId)));
  }

  public getDroneOrthos(
    construction_project_pk: string,
    querryParams: OrthoParams,
    sortModel: SortModelItem[],
    autoDetection = false,
  ) {
    let params = new HttpParams();

    if (sortModel.length) {
      const fieldName = this.serverSideTableService.changeSortColumnsName(sortModel[0]);
      params = params.append('sort_by', fieldName);
      params = params.append('sort_order', sortModel[0].sort.toUpperCase());
    }

    Object.keys(querryParams).forEach((key) => {
      params = params.append(key, querryParams[key as keyof OrthoParams]!);
    });

    return this.constructionProjectApiService.getDroneOrthos(
      construction_project_pk,
      querryParams,
      autoDetection,
      params,
    );
  }

  public getDronePanos(projectId: string, querryParams: PanoParams, sortModel: SortModelItem[]) {
    let params = new HttpParams();

    params = params.append('page', querryParams.page);
    params = params.append('page_size', querryParams.page_size);

    if (sortModel.length) {
      const fieldName = this.serverSideTableService.changeSortColumnsName(sortModel[0]);
      params = params.append('sort_by', fieldName);
      params = params.append('sort_order', sortModel[0].sort.toUpperCase());
    }

    if (querryParams.date_from) {
      params = params.append('date_from', querryParams.date_from);
    }
    if (querryParams.date_to) {
      params = params.append('date_to', querryParams.date_to);
    }
    if (querryParams.status) {
      params = params.append('status', querryParams.status);
    }

    return this.constructionProjectApiService.getDronePanos(projectId, params);
  }

  public addRover(constructionProjectId: string, data: RoverForm) {
    return this.constructionProjectApiService
      .addRover(constructionProjectId, data)
      .pipe(switchMap(() => this.getConstructionProjectAndUpdateStore(constructionProjectId)));
  }

  public deleteRover(constructionProjectId: string, rover_id: string) {
    return this.constructionProjectApiService
      .deleteRover(constructionProjectId, rover_id)
      .pipe(switchMap(() => this.getConstructionProjectAndUpdateStore(constructionProjectId)));
  }

  public roverToggle(constructionProjectId: string, data: Rover) {
    return this.constructionProjectApiService
      .roverToggle(constructionProjectId, data)
      .pipe(switchMap(() => this.getConstructionProjectAndUpdateStore(constructionProjectId)));
  }

  public createReferenceLayer(projectId: string, data: ReferenceLayer) {
    return this.referenceLayersApiService
      .createReferenceLayer(projectId, data)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result))));
  }

  public editReferenceLayer(projectId: string, layerId: string, data: ReferenceLayer) {
    return this.referenceLayersApiService
      .editReferenceLayer(projectId, layerId, data)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result))));
  }

  public deleteReferenceLayer(projectId: string, layerId: string) {
    return this.referenceLayersApiService
      .deleteReferenceLayer(projectId, layerId)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result))));
  }

  public saveSupplyPlan(projectId: string, data: SupplyPlanResponseData) {
    return this.constructionProjectApiService
      .saveSupplyPlan(projectId, data)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result))));
  }

  public saveEquipmentSpecs(projectId: string, data: EquipmentSpecs) {
    return this.constructionProjectApiService
      .saveEquipmentSpecs(projectId, data)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result))));
  }

  public saveEmptyPacks(projectId: string, data: EmptyPackReturnsResponseData) {
    return this.constructionProjectApiService
      .saveEmptyPacks(projectId, data)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result))));
  }

  public saveWbsConfiguration(projectId: string, data: WbsConfigurationScope[]) {
    return this.constructionProjectApiService
      .saveWbsConfiguration(projectId, data)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result.data))));
  }

  public publishWbsConfiguration(projectId: string) {
    return this.constructionProjectApiService
      .publishWbsConfiguration(projectId)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result))));
  }
  public resetWbsConfiguration(projectId: string) {
    return this.constructionProjectApiService
      .resetWbsConfiguration(projectId)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result))));
  }

  public updateWbsConfigurationPublished(projectId: string, data: WbsConfigEdit) {
    return this.constructionProjectApiService
      .updateWbsConfigurationPublished(projectId, data)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result))));
  }

  public bulkSaveEods(projectId: string, data: BulkEodsToSave) {
    return this.constructionProjectApiService
      .bulkSaveEods(projectId, data)
      .pipe(switchMap(() => this.getConstructionProjectAndUpdateStore(projectId)));
  }

  public lockScope(projectId: string, scope: string) {
    return this.constructionProjectApiService
      .lockScope(projectId, scope)
      .pipe(switchMap((result) => this.getConstructionProjectAndUpdateStore(projectId).pipe(map(() => result.data))));
  }

  /**
   * Function that groups calendar work weeks by year, in the range that we provided,
   * with their corresponding project work week
   *@param startDate start of our date range
   *@param endDate end of our date range
   *@returns Grouped project work weeks by year
   */
  public mappedProjectWeeksGrouped(
    startDate: Moment,
    endDate: Moment,
  ): Map<number, WeekNumberEquivalentProjectWeekType> {
    //get a starting year
    let year = startDate.clone().year();
    //starting project week
    let projectWeek = 1;
    //calendar work week for keeping track
    let isoWeekNumber = 0;

    let data: Record<number, number> = {};
    //if the week is continuing to next year
    let carryOver = false;

    //a map of a grouped project weeks
    const projectWeekNumber: Map<number, WeekNumberEquivalentProjectWeekType> = new Map();

    while (startDate.isSameOrBefore(endDate)) {
      const weekNumber = startDate.clone().isoWeek();
      const dateYear = startDate.clone().year();

      //check if it's new week in the same year
      if (weekNumber !== isoWeekNumber && year === dateYear) {
        //if we didn't already added this week to this year
        if (!data[weekNumber]) {
          data[weekNumber] = projectWeek;
        } else {
          //if we did, this means that this week is in the next year
          //NOTE: only happens for the first week of the year
          carryOver = true;
        }
        //for keeping track that we are not finished with this week
        isoWeekNumber = weekNumber;
        projectWeek++;
      }
      if (year !== dateYear) {
        //if we got to the next year, set entry of the last in the map
        projectWeekNumber.set(year, data);
        //increase year and reset the data for next year
        year++;
        data = {};
        //if the week continued in the next year
        if (carryOver) {
          //apply the first week of the next year
          data[weekNumber] = projectWeek - 1;
          //reset the flag
          carryOver = false;
        }
      }
      startDate.add(1, 'day');
    }
    //when we get out of the while condition, apply the last year that we mapped
    projectWeekNumber.set(year, data);

    return projectWeekNumber;
  }

  public getDailyReport(idProject: string, date: string, trendTimeFrame: number, forecastTimeFrame: number) {
    return this.constructionProjectApiService
      .getDailyReport(idProject, date, trendTimeFrame, forecastTimeFrame)
      .pipe(map((response) => response.data));
  }

  public verifyProjectInService(constructionProjectId: string) {
    return this.constructionProjectApiService
      .verifyProjectInService(constructionProjectId)
      .pipe(
        switchMap((result) => this.getConstructionProjectAndUpdateStore(constructionProjectId).pipe(map(() => result))),
      );
  }

  public mapProjectFeatures(features: ConstructionProjectFeaturesType[]) {
    const projectFeatures = features ? features : listOfFeatures;

    return projectFeatures
      .map((feature) => {
        return {
          name: feature.name,
          owner_enabled: feature.owner_enabled || false,
          epc_enabled: feature.epc_enabled || false,
          supplier_enabled: feature.supplier_enabled || false,
          aerial_data: feature.aerial_data ? { ...feature.aerial_data } : feature.aerial_data,
          ground_data: feature.ground_data ? { ...feature.ground_data } : feature.ground_data,
          id: constructionProjectFeaturesOrder[feature.name],
        };
      })
      .sort((a, b) => a.id - b.id);
  }
}
